import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { usePersistentState, markNavigationPreserve, consumeNavigationPreserve, clearNamespace } from "../../hooks/persistence";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { CommonService } from "../../Service/CommonService";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useRoute } from "../Common/route";
import Constant from "../../Constant";
import { SupportApiService } from "../../Service/SupportApiService";
import StatusDropdown from "../Common/StatusDropdown";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import EditIcon from '../../assets/images/icons/EditIcon.svg';
import TrashIcon from "../../assets/images/icons/Trash_Black.svg";
import DeleteItemIcon from "../../assets/images/icons/DeleteItem.png";
import Swal from "sweetalert2";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import SupportFaqPanel from "./SupportFaqPanel";
import { TagsInput } from "react-tag-input-component";
import CheckIcon from '@mui/icons-material/Check';
import { InventoryApiService } from "../../Service/InventoryApiService";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DataGridLoader from "../Common/DataGridLoader";
import MultiSelectChipInput from "../Common/MultiSelectChipInput";
import { Service } from "../../Service/ApiService";
import { Accordion } from "react-bootstrap";

interface LocationState {
    data: string;
}

const LogisticRequestListing: React.FC = () => {
    const route = useRoute();
    const history = useHistory();
    const controllerRef = useRef<AbortController | null>(null);
    const state = history.location.state as LocationState;
    const [isLoading, setIsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [allRequests, setAllRequests] = useState<any[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const toggleDropdown = () => { setShowDropdown((prev) => !prev); }
    const [viewFaq, setViewFaq] = useState(false);
    const handleCloseFaq = () => setViewFaq(false);
    const handleShowFaq = () => setViewFaq(true);
    const mainInputRef = useRef<HTMLDivElement | null>(null);
    const scheduleInputRef = useRef<HTMLDivElement | null>(null);
    const datePickerRef = useRef<HTMLDivElement | null>(null);
    const scheduleDatePickerRef = useRef<HTMLDivElement | null>(null);
    const [isMainDatePickerOpen, setIsMainDatePickerOpen] = useState(false);
    const [isScheduleDatePickerOpen, setIsScheduleDatePickerOpen] = useState(false);
    const companyOptionsControllerRef = useRef<AbortController | null>(null);
    const [allCompanyOptions, setAllCompanyOptions] = useState<string[]>([]);
    const [allProducerOptions, setAllProducerOptions] = useState<string[]>([]);
    const [allLotNoOptions, setAllLotNoOptions] = useState<string[]>([]);
    const [allCarrierOptions, setAllCarrierOptions] = useState<string[]>([]);

    const initialFilter = {
        requestType: null,
        requestNumber: null,
        companyName: null,
        name: null,
        createdDate: null,
        totalVolume: null,
        totalProof: null,
        totalProofGallon: null,
        startDate: null,
        endDate: null,
        carrier: null,
        facilityFrom: null,
        facilityTo: null,
        producer: null,
        lotNo: null,
        scheduleDate: null,
        scheduleStartDate: null,
        scheduleEndDate: null
    };

    const _filterInitRef = useRef(false);
    if (!_filterInitRef.current) {
        _filterInitRef.current = true;
        if (!consumeNavigationPreserve("logistic-request")) {
            clearNamespace("logistic-request");
        }
    }

    const [page, setPage] = usePersistentState<number>("logistic-request", "page", 0);
    const [pageSize, setPageSize] = usePersistentState<number>("logistic-request", "pageSize", 50);
    const [sortField, setSortField] = usePersistentState<string>("logistic-request", "sortField", "CreatedModifiedDate");
    const [sortDirection, setSortDirection] = usePersistentState<string>("logistic-request", "sortDirection", "desc");
    const [filter, setFilter] = usePersistentState<any>("logistic-request", "filter", initialFilter);
    const [sortModel, setSortModel] = usePersistentState<any[]>("logistic-request", "sortModel", []);
    const [selectedCompanies, setSelectedCompanies] = usePersistentState<string[]>("logistic-request", "selectedCompanies", []);
    const [selectedProducers, setSelectedProducers] = usePersistentState<string[]>("logistic-request", "selectedProducers", []);
    const [selectedLotNos, setSelectedLotNos] = usePersistentState<string[]>("logistic-request", "selectedLotNos", []);
    const [selectedCarriers, setSelectedCarriers] = usePersistentState<string[]>("logistic-request", "selectedCarriers", []);
    const [mainPickerStyle, setMainPickerStyle] = useState<React.CSSProperties>({});
    const [schedulePickerStyle, setSchedulePickerStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
        if (companyOptionsControllerRef.current) companyOptionsControllerRef.current.abort();
        const controller = new AbortController();
        companyOptionsControllerRef.current = controller;
        Service.fetchCompaniesList()
            .then((res: any) => {
                const names: string[] = (res.data || []).map((c: any) => c.companyName).filter(Boolean).sort();
                setAllCompanyOptions(names);
            })
            .catch(() => {});

        InventoryApiService.getInventoryFilterOptions(null)
            .then((res: any) => {
                const opts = res.data;
                setAllProducerOptions(opts.producers || []);
                setAllLotNoOptions(opts.lotNos || []);
            })
            .catch(() => {});
        
        Service.getAllCarrierList()
            .then((res: any) => {
                const names: string[] = (res.data || [])
                    .filter((c: any) => c.isActive && !c.isDelete)
                    .map((c: any) => c.name?.trim())
                    .filter(Boolean)
                    .sort();
                setAllCarrierOptions(names);
            })
            .catch(() => {});
    }, []);

    const formatDate = (date: Date | null) => {
        if (!date) return null;

        const parsedDate =
            typeof date === "string"
                ? new Date(date + "T00:00:00")
                : date;

        return parsedDate.toLocaleDateString("en-CA");
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            // MAIN PICKER
            if (
                isMainDatePickerOpen &&
                datePickerRef.current &&
                !datePickerRef.current.contains(target) &&
                mainInputRef.current &&
                !mainInputRef.current.contains(target)
            ) {
                setIsMainDatePickerOpen(false);
            }

            // SCHEDULE PICKER
            if (
                isScheduleDatePickerOpen &&
                scheduleDatePickerRef.current &&
                !scheduleDatePickerRef.current.contains(target) &&
                scheduleInputRef.current &&
                !scheduleInputRef.current.contains(target)
            ) {
                setIsScheduleDatePickerOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMainDatePickerOpen, isScheduleDatePickerOpen]);

    useEffect(() => {
        if (!isMainDatePickerOpen && !isScheduleDatePickerOpen) return;

        const handleScroll = () => {
            updatePickerPositions();
        };

        const handleResize = () => {
            updatePickerPositions();
        };

        // window scroll (body scroll)
        window.addEventListener("scroll", handleScroll, true);
        // 👆 useCapture = true is IMPORTANT (captures parent scroll too)

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleResize);
        };
    }, [isMainDatePickerOpen, isScheduleDatePickerOpen]);

    useEffect(() => {
        fetchAllLogisticRequests(page, pageSize, filter, sortField, sortDirection);
    }, []);

    const getFixedPickerPosition = (
        triggerEl: HTMLDivElement | null,
        pickerWidth: number = 700,
        gap: number = 8
    ) => {
        if (!triggerEl) return {};

        const rect = triggerEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        let left = rect.left;
        let top = rect.bottom + gap;

        if (left + pickerWidth > viewportWidth - 16) {
            left = viewportWidth - pickerWidth - 16;
        }

        if (left < 16) {
            left = 16;
        }

        return {
            position: "fixed" as const,
            top: `${top}px`,
            left: `${left}px`,
            zIndex: 9999,
        };
    };

    const handleMainPickerToggle = () => {
        if (!isMainDatePickerOpen) {
            setMainPickerStyle(getFixedPickerPosition(mainInputRef.current, 700));
        }
        setIsMainDatePickerOpen((prev) => !prev);
        setIsScheduleDatePickerOpen(false);
    };

    const handleSchedulePickerToggle = () => {
        if (!isScheduleDatePickerOpen) {
            setSchedulePickerStyle(getFixedPickerPosition(scheduleInputRef.current, 700));
        }
        setIsScheduleDatePickerOpen((prev) => !prev);
        setIsMainDatePickerOpen(false);
    };

    const updatePickerPositions = () => {
        if (isMainDatePickerOpen) {
            setMainPickerStyle(
                getFixedPickerPosition(mainInputRef.current, 700)
            );
        }

        if (isScheduleDatePickerOpen) {
            setSchedulePickerStyle(
                getFixedPickerPosition(scheduleInputRef.current, 700)
            );
        }
    };

    // final selected values
    const [selectedDateRange, setSelectedDateRange] = useState<any>({
        startDate: filter.startDate ?? null,
        endDate: filter.endDate ?? null,
        key: "selection",
    });

    const [selectedScheduleDateRange, setSelectedScheduleDateRange] = useState<any>({
        startDate: filter.scheduleStartDate ?? null,
        endDate: filter.scheduleEndDate ?? null,
        key: "selection",
    });

    // temp values for picker
    const [tempRange, setTempRange] = useState<any>({
        startDate: filter.startDate ?? null,
        endDate: filter.endDate ?? null,
        key: "selection",
    });

    const [tempScheduleRange, setTempScheduleRange] = useState<any>({
        startDate: filter.scheduleStartDate ?? null,
        endDate: filter.scheduleEndDate ?? null,
        key: "selection",
    });

    // main date picker change
    const handleTempChange = (start: Date | null, end: Date | null) => {
        setTempRange({
            startDate: start,
            endDate: end,
            key: "selection",
        });
    };

    // schedule date picker change
    const handleScheduleDateChange = (start: Date | null, end: Date | null) => {
        setTempScheduleRange({
            startDate: start,
            endDate: end,
            key: "selection",
        });
    };

    // main date picker actions
    const handleMainClear = () => {
        setTempRange({
            startDate: null,
            endDate: null,
            key: "selection",
        });
        setSelectedDateRange({
            startDate: null,
            endDate: null,
            key: "selection",
        });
        setIsMainDatePickerOpen(false);

        const tempFilter = {
            ...filter,
            startDate: null,
            endDate: null,
        };

        setFilter(tempFilter);
    };

    const handleMainCancel = () => {
        setTempRange(selectedDateRange);
        setIsMainDatePickerOpen(false);
    };

    const handleMainOk = () => {
        setSelectedDateRange(tempRange);
        setIsMainDatePickerOpen(false);

        const tempFilter = {
            ...filter,
            startDate: formatDate(tempRange.startDate),
            endDate: formatDate(tempRange.endDate),
        };

        setFilter(tempFilter);
    };

    // schedule date picker actions
    const handleScheduleClear = () => {
        setTempScheduleRange({
            startDate: null,
            endDate: null,
            key: "selection",
        });
        setSelectedScheduleDateRange({
            startDate: null,
            endDate: null,
            key: "selection",
        });
        setIsScheduleDatePickerOpen(false);

        const tempFilter = {
            ...filter,
            scheduleStartDate: null,
            scheduleEndDate: null,
        };

        setFilter(tempFilter);
    };

    const handleScheduleCancel = () => {
        setTempScheduleRange(selectedScheduleDateRange);
        setIsScheduleDatePickerOpen(false);
    };

    const handleScheduleOk = () => {
        setSelectedScheduleDateRange(tempScheduleRange);
        setIsScheduleDatePickerOpen(false);

        const tempFilter = {
            ...filter,
            scheduleStartDate: formatDate(tempScheduleRange.startDate),
            scheduleEndDate: formatDate(tempScheduleRange.endDate),
        };
        setFilter(tempFilter);
    };

    const toggleSort = (field) => {
        const current = sortModel.find((s) => s.field === field);

        let newSort = "asc";

        if (current?.sort === "asc") {
            newSort = "desc";
        }

        setSortModel([{ field, sort: newSort }]);

        fetchAllLogisticRequests(page, pageSize, filter, field, newSort);

    };

    function TwoSortHeader({ title1, title2, field1, field2, sortModel, toggleSort }) {

        const getIcon = (field) => {
            const item = sortModel.find((s) => s.field === field);

            if (!item) return "";
            if (item.sort === "asc") {
                return (
                    <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall MuiDataGrid-sortIcon css-ptiqhd-MuiSvgIcon-root sorting-icon"
                        focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="ArrowUpwardIcon">
                        <path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"></path>
                    </svg>
                );
            }
            if (item.sort === "desc") {
                return (
                    <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall MuiDataGrid-sortIcon css-ptiqhd-MuiSvgIcon-root sorting-icon"
                        focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="ArrowDownwardIcon">
                        <path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"></path>
                    </svg>
                );
            }
        };

        return (
            <div className="d-flex flex-column h-100 justify-content-center list-data-wrapper gap-1">

                <div
                    style={{ cursor: "pointer", fontWeight: 400, fontSize: "14px", lineHeight: "20px", color: "#6f6f6f" }}
                    onClick={() => toggleSort(field1)}
                >
                    {title1} {getIcon(field1)}
                </div>

                <div
                    style={{ cursor: "pointer", fontWeight: 400, fontSize: "14px", lineHeight: "20px", color: "#6f6f6f" }}
                    onClick={() => toggleSort(field2)}
                >
                    {title2} {getIcon(field2)}
                </div>

            </div>
        );
    }

    function TwoLineCell({ value1, value2 }) {

        return (
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", paddingBlock: "8px", height: "100%" }}>
                <Tooltip arrow title={value1}><span style={{ fontSize: 14, color: "#333333", fontWeight: "400", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", }}>{value1}</span></Tooltip>
                <Tooltip arrow title={value2}>
                    <span style={{ fontSize: 14, color: "#333333", fontWeight: "400", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", }}>
                        {value2}
                    </span>
                </Tooltip>
            </div>
        );
    }

    const isFilterApplied = Object.values(filter).some(
        (value) => value !== null && value !== "" && value !== undefined
    );

    const handleFilterReset = (field: keyof any) => {
        const tempFilter = {
            ...filter,
            [field]: null
        };

        setFilter(tempFilter);
    };

    const handleClear = () => {
        handleMainClear();
        handleScheduleClear();
        const tempFilter = {
            startDate: null,
            endDate: null,
            scheduleStartDate: null,
            scheduleEndDate: null,
            carrier: null,
            companyName: null,
            producer: null,
            lotNo: null,
            totalProofGallon: null,
            totalVolume: null
        };

        setSelectedCompanies([]);
        setSelectedProducers([]);
        setSelectedLotNos([]);
        setSelectedCarriers([]);
        setFilter(tempFilter);
        fetchAllLogisticRequests(page, pageSize, tempFilter, sortField, sortDirection);
    }

    const handleFilterChange = (event: any, field: keyof any) => {
        let value;
        if (field === 'carrier' || field === 'companyName' || field === 'producer' || field === 'lotNo')
            value = event.join(",");
        else
            value = event.target.value;

        const tempFilter = {
            ...filter,
            [field]: value
        };

        // Update the filter state
        setFilter(tempFilter);
    };

    const handleApply = () => {
        const merged = {
            ...filter,
            companyName: selectedCompanies.length > 0 ? selectedCompanies.join(",") : null,
            producer: selectedProducers.length > 0 ? selectedProducers.join(",") : null,
            lotNo: selectedLotNos.length > 0 ? selectedLotNos.join(",") : null,
            carrier: selectedCarriers.length > 0 ? selectedCarriers.join(",") : null
        };
        setFilter(merged);
        fetchAllLogisticRequests(page, pageSize, merged, sortField, sortDirection);
    };

    function formatStatus(status: string): string {
        const formattedStatus = status
            .split(/[_\\]/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");

        return formattedStatus;
    }

    const CellWithTooltip = ({ value }: { value: string | number }) => {
        return (
            <Tooltip arrow title={value?.toString()}>
                <span>{value?.toString()}</span>
            </Tooltip>
        );
    };

    const handleRowClick = (params) => {
        const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";
        markNavigationPreserve("logistic-request");
        history.push(`${linkPrefix}/view-customer-service-request/${params.row.id}`);
    }

    const fetchAllLogisticRequests = async (page: number, size: number, filter: any, sortField: any, sortDirection: any) => {
        const controller = new AbortController();
        const signal = controller.signal;
        try {
            setIsLoading(true);
            setAllRequests([]);

            // Cancel the previous request if any
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            // Store the current controller to cancel the request if needed
            controllerRef.current = controller;

            // Important: Adjust the page number for backend (if it uses 0-based indexing)
            const response: any = await SupportApiService.fetchAllLogisticRequests(state?.data,
                page,
                size,
                filter,
                sortField,
                sortDirection,
                signal
            );
            setAllRequests(response.data.content);
            setTotalCount(response.data.totalElements);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error("Error fetching data:", error);
        }
    };

    const exportMyLogisticRequest = async (page: any, size: any, filter: any, sortField: any, sortDirection: any) => {
        const controller = new AbortController();
        const signal = controller.signal;

        try {
            setIsLoading(true);

            // Cancel the previous request if any
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            // Store the current controller to cancel the request if needed
            controllerRef.current = controller;
            InventoryApiService.exportMyLogisticRequest(state?.data, filter, sortField, sortDirection, signal);
            CommonService.Toast.fire({
                title: "Your export is in progress. We'll notify you when it's ready!",
                icon: "success",
            });
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };

    const onPaginationModelChange = (newPaginationModel) => {
        setPage(newPaginationModel.page);
        setPageSize(newPaginationModel.pageSize);

        // Fetch using the latest sorting values
        fetchAllLogisticRequests(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection);
    };

    const handleSortChange = (sortModel) => {
        if (sortModel.length > 0) {
            const newSortField = sortModel[0].field;
            const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

            setSortField(newSortField);
            setSortDirection(newSortDirection);

            fetchAllLogisticRequests(page, pageSize, filter, newSortField, newSortDirection);
        }
    };

    const handleEditServiceRequest = (id: any) => {
        const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";
        markNavigationPreserve("logistic-request");
        history.push(`${linkPrefix}/edit-customer-service-request/${id}`);
    }

    const deleteServiceRequest = async (id: any) => {

        Swal.fire({
            html: `
                <style>
                  .custom-swal .icon-container {
                      background-color: #ffebee;
                      border-radius: 50%;
                      width: 105px;
                      height: 105px;
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      margin: 0 auto 20px;
                    }
                    .custom-swal .icon-container i {
                      color: #ff3e4e;
                      font-size: 24px;
                    }
                    .custom-swal h2 {
                      font-size: 20px;
                      margin-bottom: 10px;
                      margin-top: 20px;
                    }
                    .custom-swal p {
                      font-size: 14px;
                      color: #666;
                      margin-bottom: 20px;
                    }
                    .custom-swal .button-container {
                      margin-top: 40px;
                      display: flex;
                      justify-content: center;
                      gap: 20px;
                    }
                    .custom-swal button {
                      border: none;
                      padding: 10px 50px;
                      border-radius: 30px;
                      font-size: 14px;
                      cursor: pointer;
                    }
                    .custom-swal .cancel-btn {
                      background-color: #e0e0e0;
                      color: #333;
                    }
                    .custom-swal .delete-btn {
                      background-color: #ec2323;
                      color: white;
                    }
                    
                </style>
                <div class="custom-swal">
          
                <div class="icon-container" id="iconContainer">
                  </div>
          
                  <h2>Are you sure?</h2>
                  <p>Remove service request</p>
                  <div class="button-container">
                    <button class="cancel-btn" id="cancelBtn">Cancel</button>
                    <button class="delete-btn" id="deleteBtn">Remove</button>
                  </div>
                </div>
              `,
            showConfirmButton: false,
            showCancelButton: false,
            showCloseButton: true,
            focusCancel: true,
            customClass: {
                popup: "custom-popup",
            },
            backdrop: `rgba(0,0,0,0.4)`,
            width: "500px",
            background: "#f8f8f8",
            didOpen: () => {
                // Add the image after the popup has been rendered
                const iconContainer = Swal.getPopup()!.querySelector("#iconContainer");
                if (iconContainer) {
                    const img = document.createElement("img");
                    img.src = DeleteItemIcon;
                    img.width = 47;
                    img.height = 51;
                    iconContainer.appendChild(img);
                }

                // Add event listeners to the buttons
                const cancelBtn = Swal.getPopup()!.querySelector("#cancelBtn");
                const deleteBtn = Swal.getPopup()!.querySelector("#deleteBtn");

                cancelBtn?.addEventListener("click", () => {
                    Swal.close();
                });

                deleteBtn?.addEventListener("click", async () => {
                    // Remove the item from the cart locally

                    try {
                        const response = await SupportApiService.deleteCustomerServiceRequest(id);

                        // After removing the service request, show this message
                        setTimeout(() => {
                            CommonService.Toast.fire({
                                title: "Service request removed successfully",
                                icon: "success",
                            });

                        }, 1000);
                        fetchAllLogisticRequests(page, pageSize, filter, sortField, sortDirection);
                    } catch (error) {
                        console.error("Error deleting service request:", error);
                    }

                    Swal.close();
                });
            },
        });

    }

    const columns: GridColDef[] = [
        {
            field: "carrierColumn",
            headerName: "Carrier / Schedule Date",
            flex: 1,
            minWidth: 160,
            sortable: false,
            headerClassName: "table-header border-0 my-2 h-100",

            renderHeader: () => (
                <TwoSortHeader
                    title1="Carrier"
                    title2="Schedule Date"
                    field1="carrier"
                    field2="scheduleDate"
                    sortModel={sortModel}
                    toggleSort={toggleSort}
                />
            ),

            renderCell: (params: any) => (
                <TwoLineCell
                    value1={params.row.carrier}
                    value2={params.row.scheduleDate}
                />
            )
        },
        {
            field: "facility",
            headerName: "Facility",
            flex: 1,
            minWidth: 160,
            headerClassName: "table-header border-0 my-2 h-100",
            sortable: false,
            renderHeader: () => (
                <TwoSortHeader
                    title1="Facility From"
                    title2="Facility To"
                    field1="facilityFrom"
                    field2="facilityTo"
                    sortModel={sortModel}
                    toggleSort={toggleSort}
                />
            ),

            renderCell: (params: any) => (
                <TwoLineCell
                    value1={params.row.facilityFrom}
                    value2={params.row.facilityTo}
                />
            )
        },
        {
            field: "companyName",
            headerName: "Account Owner",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 160,
            sortable: true,
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) =>
                <div className="d-flex gap-2 align-items-center h-100" style={{ fontSize: 14, color: "##333333", fontWeight: "400" }}>
                    <span>
                        <CellWithTooltip value={params.value} />
                    </span>
                </div>,
        },
        {
            field: "producerColumn",
            headerName: "Producer",
            flex: 1,
            minWidth: 160,
            headerClassName: "table-header border-0 my-2 h-100",
            sortable: false,
            renderHeader: () => (
                <TwoSortHeader
                    title1="Producer"
                    title2="LOT(s)"
                    field1="producer"
                    field2="lotNo"
                    sortModel={sortModel}
                    toggleSort={toggleSort}
                />
            ),

            renderCell: (params: any) => (
                <TwoLineCell
                    value1={params.row.producer}
                    value2={params.row.lotNo}
                />
            )
        },
        {
            field: "volumeColumn",
            headerName: "Volume",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 140,
            sortable: false,
            renderHeader: () => (
                <TwoSortHeader
                    title1="Volume"
                    title2="Proof Gallon"
                    field1="totalVolume"
                    field2="totalProofGallon"
                    sortModel={sortModel}
                    toggleSort={toggleSort}
                />
            ),

            renderCell: (params: any) => (
                <TwoLineCell
                    value1={params.row?.totalVolume || "-"}
                    value2={params.row?.totalProofGallon || "-"}
                />
            )
        },
        {
            field: "quantity",
            headerName: "Quantity",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 100,
            sortable: true,
            renderHeader: () => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        Quantity
                    </Typography>
                </div>
            ),

            renderCell: (params: any) => (
                <div style={{ display: "flex", flexDirection: "column", lineHeight: "18px", justifyContent: "center", height: "100%", paddingBlock: "8px", fontSize: 14, color: "##333333", fontWeight: "400" }}>
                    <span>{params.row.quantity}</span>
                </div>
            )
        },
        {
            field: "scanned",
            headerName: "Scanned",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 100,
            sortable: true,
            renderHeader: () => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        Scanned
                    </Typography>
                </div>
            ),

            renderCell: (params: any) => (
                <div style={{ display: "flex", flexDirection: "column", lineHeight: "18px", justifyContent: "center", height: "100%", paddingBlock: "8px", fontSize: 14, color: "##333333", fontWeight: "400" }}>
                    <span>{params.row.scanned}</span>
                </div>
            )
        },
        {
            field: "progress",
            headerName: "Progress",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderHeader: () => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        Progress
                    </Typography>
                </div>
            ),

            renderCell: (params: any) => {
                const progress = params.row.quantity > 0 ? Math.round((params.row.scanned / params.row.quantity) * 100) : 0;

                return (
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: "18px", justifyContent: "center", height: "100%", paddingBlock: "8px", fontSize: 14, color: "##333333", fontWeight: "400" }}>
                        <span>{progress}%</span>
                    </div>
                );
            }
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1,
            minWidth: 150,
            sortable: true,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <div className="d-flex gap-2 align-items-center h-100" style={{ fontSize: 14, color: "##333333", fontWeight: "400" }}>
                        {params.value ? (
                            <div
                                className="description-wrapper"
                                dangerouslySetInnerHTML={{
                                    __html: params.value,
                                }}
                            />
                        ) : (
                            "-"
                        )}
                </div>
            ),
        },
        {
            field: "name",
            headerName: "User",
            flex: 1,
            minWidth: 150,
            sortable: true,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <div className="d-flex gap-2 align-items-center h-100" style={{ fontSize: 14, color: "##333333", fontWeight: "400" }}>
                    <span>
                        <CellWithTooltip value={params.value} />
                    </span>
                </div>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            headerClassName: "table-header border-0 my-2 h-100",
            minWidth: 240,
            cellClassName: "status-badge d-flex align-items-center",
            flex: 1,
            sortable: true,
            renderCell: (params: any) => (
                <div
                    className="cell-with-dropdown"
                    onClick={(e) => e.stopPropagation()}
                >
                    <StatusDropdown
                        currentStatus={formatStatus(params.value)}
                        onStatusChange={(newStatus) => { }}
                        disableCondition={true}
                        allowedLabels={[]}
                    />
                    {(params.row.status === "REJECTED" || params.row.status === "CANCELLED") && params.row.rejectionNote && (
                        <Tooltip
                            title={`Rejection Note: ${params.row.rejectionNote}`}
                            arrow
                        >
                            <StickyNote2Icon
                                className="note-icon cursor-pointer"
                                style={{ color: "gray" }}
                            />
                        </Tooltip>
                    )}
                </div>
            ),
            renderHeader: (params) => (
                <>
                    <div>
                        <Typography
                            variant="body2"
                            sx={{ color: "#6F6F6F", fontSize: "14px" }}
                        >
                            {params.colDef.headerName}
                        </Typography>
                    </div>
                </>
            ),
        },
        {
            field: "action",
            headerName: "Action",
            sortable: false,
            minWidth: 120,
            flex: 1,
            headerClassName: "table-header border-0 my-2 h-100",
            renderCell: (params: any) => {
                const isSuperAdminAndUser =
                    params?.row?.roleName === "SUPER_ADMIN" && CommonService.isUser();

                const isReviewStage =
                    params?.row?.requestType !== Constant.CustomerService.TASTING &&
                    (params?.row?.status === Constant.CustomerServiceRequestStatus.PENDING_REVIEW ||
                        params?.row?.status === Constant.CustomerServiceRequestStatus.UNDER_REVIEW ||
                        params?.row?.status === Constant.CustomerServiceRequestStatus.SEND_TO_OWNER);

                const isTastingStage =
                    !CommonService.isUser() && params?.row?.requestType === Constant.CustomerService.TASTING &&
                    (params?.row?.status === Constant.CustomerServiceRequestStatus.SEND_TO_WAREHOUSE ||
                        params?.row?.status === Constant.CustomerServiceRequestStatus.ADMIN_ACCEPTED_FOR_WAREHOUSE);


                const isSellRequestAllowed =
                    params?.row?.requestType === Constant.CustomerService.SELL_REQUEST &&
                    !["CANCELLED", "REJECTED", "DELETED", "UNPUBLISHED"].includes(
                        params?.row?.status
                    );

                const isWantedToBuyAllowed =
                    params?.row?.requestType === Constant.CustomerService.WANTED_TO_BUY &&
                    !["CANCELLED", "REJECTED", "DELETED", "COMPLETED"].includes(
                        params?.row?.status
                    );

                const isCustomSampleAllowed =
                    params?.row?.requestType === Constant.CustomerService.SAMPLE_REQUEST &&
                    params?.row?.status === Constant.CustomerServiceRequestStatus.SEND_TO_OWNER;

                return (
                    <>
                        {(((params?.row?.requestType == Constant.CustomerService.SAMPLE_REQUEST || params?.row?.requestType !== Constant.CustomerService.TASTING ||
                            params?.row?.requestType == Constant.CustomerService.CUSTOM_SAMPLE_REQUEST) &&
                            ['PENDING_REVIEW', 'UNDER_REVIEW', 'SEND_TO_OWNER'].includes(params?.row?.status)) ||
                            ((!isSuperAdminAndUser || params?.row?.requestType == Constant.CustomerService.RE_CONTAINERIZATION ||
                                params?.row?.requestType == Constant.CustomerService.BARREL_HEAD_PAINTING ||
                                params?.row?.requestType !== Constant.CustomerService.TASTING) && isReviewStage || isTastingStage) || isSellRequestAllowed || isWantedToBuyAllowed) && (
                                <div
                                    className="d-flex align-items-center gap-2 h-100"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Edit Button */}
                                    <Tooltip title="Edit" arrow>
                                        <IconButton
                                            aria-label="edit"
                                            size="small"
                                            onClick={() => handleEditServiceRequest(params?.row?.id)}
                                            disabled={
                                                !(params?.row?.requestType == Constant.CustomerService.SAMPLE_REQUEST || params?.row?.requestType == Constant.CustomerService.CUSTOM_SAMPLE_REQUEST) &&
                                                params?.row?.requestType !== Constant.CustomerService.SELL_REQUEST && params?.row?.requestType !== Constant.CustomerService.WANTED_TO_BUY &&
                                                (isSuperAdminAndUser && params?.row?.requestType != Constant.CustomerService.RE_CONTAINERIZATION && params?.row?.requestType != Constant.CustomerService.BARREL_HEAD_PAINTING && params?.row?.requestType != Constant.CustomerService.TASTING) &&
                                                !isCustomSampleAllowed // allow edit when custom sample + send_to_owner
                                            }
                                        >
                                            <img
                                                src={EditIcon}
                                                className="cursor-pointer"
                                                alt="EditIcon"
                                            />
                                        </IconButton>
                                    </Tooltip>

                                    {/* Delete Button (only for certain status/roles) */}
                                    {(((params?.row?.requestType == Constant.CustomerService.SAMPLE_REQUEST || params?.row?.requestType == Constant.CustomerService.CUSTOM_SAMPLE_REQUEST) || (!isSuperAdminAndUser)) && ['PENDING_REVIEW', 'UNDER_REVIEW', 'SEND_TO_OWNER'].includes(params?.row?.status)) && (
                                        <Tooltip title="Delete" arrow>
                                            <IconButton
                                                aria-label="delete"
                                                size="small"
                                                onClick={() => deleteServiceRequest(params?.row?.id)}
                                                disabled={!(params?.row?.requestType == Constant.CustomerService.SAMPLE_REQUEST || params?.row?.requestType == Constant.CustomerService.CUSTOM_SAMPLE_REQUEST) && isSuperAdminAndUser}
                                            >
                                                <img
                                                    src={TrashIcon}
                                                    height={22}
                                                    width={22}
                                                    alt="DeleteIcon"
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                    </>
                );
            },
        },
        { field: "carrier" },
        { field: "scheduleDate" },
        { field: "facilityFrom" },
        { field: "facilityTo" },
        { field: "producer" },
        { field: "lotNo" },
        { field: "totalVolume" },
        { field: "totalProofGallon" }
    ].filter(Boolean) as GridColDef[];

    return (
        <>
            <div className="usersList" style={{ paddingBottom: CommonService.isAdmin() ? 51 : "" }}>
                <div className="usersList__header--wrapper d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <IconButton
                            onClick={() => route('/master/logistic-requests')}
                            aria-label="ArrowBackIcon"
                            sx={{
                                backgroundColor: "#FFF",
                                "&:hover": { backgroundColor: "#FFF" },
                            }}
                        >
                            <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                        </IconButton>
                        <div className="header--title me-2">
                            <h1 className="title mb-0 ms-2">{state?.data}</h1>
                        </div>
                    </div>
                    <div className="divider mx-2"></div>
                    <div className="d-flex align-items-center gap-3">
                        
                        {/* Export Button*/}
                        {allRequests.length > 0 || isLoading ?
                            <>
                                <button title="Export to Excel"
                                    className="primary-btn d-inline-flex align-items-center"
                                    style={{ paddingTop: "4px", paddingLeft: 11 }}
                                    onClick={(e) => {
                                        e.currentTarget.blur(); // Removes focus after clicking
                                        exportMyLogisticRequest(page, pageSize, filter, sortField, sortDirection);
                                    }}
                                >
                                    <FileDownloadOutlinedIcon sx={{ marginTop: "5.5px" }} />
                                    Export
                                </button>
                            </>
                        : null}
                    </div>
                    <div className="ms-auto d-flex gap-2 align-items-center">
                        <div>
                            <button className="support-faq-btn" onClick={handleShowFaq}>
                                Click here for FAQs and Tutorials
                            </button>
                        </div>
                    </div>
                </div>

                <div className="user-table-container position-relative report-module">
                    {viewFaq && <SupportFaqPanel handleCloseFaq={handleCloseFaq} />}
                        <Accordion defaultActiveKey="0" className="filter-accordion">
                            <Accordion.Item eventKey="0" className="filter-accordion-item">
                                <Accordion.Header className="filter-accordion-header">
                                    <span className="accordion-filter-title">Filters</span>
                                    <button
                                        className="filter-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleClear();
                                        }}
                                    >
                                        Reset All
                                    </button>
                                </Accordion.Header>
                                <Accordion.Body className="filter-accordion-body p-0">
                                    <div className="filter-panel-accordion">

                                        <div className="filter-body">

                                            {/* Date Range */}
                                            <div className="filter-section position-relative">
                                                <div className="section-header">
                                                    <h4>Date range</h4>
                                                    <button className="section-btn" type="button" onClick={handleMainClear}>Reset</button>
                                                </div>
                                                {/* <label htmlFor="dateRange">Filter by Date Range</label> */}
                                                <div className="date-input-wrapper" ref={mainInputRef}>
                                                    <input
                                                        type="text"
                                                        placeholder="Select date range"
                                                        readOnly
                                                        className="form-control"
                                                        value={
                                                            selectedDateRange.startDate && selectedDateRange.endDate
                                                                ? `${formatDate(selectedDateRange.startDate)} - ${formatDate(selectedDateRange.endDate)}`
                                                                : ""
                                                        }
                                                        onClick={handleMainPickerToggle}
                                                    />
                                                    <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M13.5 1.51172H12.7266V0H11.25V1.51172H3.72656V0H2.25V1.51172H1.47656C0.667969 1.51172 0 2.17969 0 3.02344V15.0117C0 15.8203 0.667969 16.5234 1.47656 16.5234H13.5C14.3086 16.5234 14.9766 15.8203 14.9766 15.0117V3.02344C14.9766 2.17969 14.3086 1.51172 13.5 1.51172ZM13.5 15.0117H1.47656V6.75H13.5V15.0117ZM13.5 5.27344H1.47656V3.02344H13.5V5.27344Z" fill="#9CA3AF" />
                                                    </svg>
                                                </div>

                                                {isMainDatePickerOpen && (
                                                    <div className="datepicker-menu" ref={datePickerRef} style={mainPickerStyle}>
                                                        <DateRange
                                                            ranges={[tempRange]}
                                                            editableDateInputs={true}
                                                            dropdownMode="scroll"
                                                            onChange={(item: any) =>
                                                                handleTempChange(
                                                                    item.selection.startDate ?? null,
                                                                    item.selection.endDate ?? null
                                                                )
                                                            }
                                                            moveRangeOnFirstSelection={false}
                                                            months={2}
                                                            direction="horizontal"
                                                            showDateDisplay={false}
                                                        />
                                                        <div className="datepicker-menu-bottom">
                                                            <button type="button" onClick={handleMainCancel} className="datepicker-btn">
                                                                CANCEL
                                                            </button>
                                                            <button type="button" onClick={handleMainOk} className="datepicker-btn">
                                                                OK
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Carrier */}
                                            <div className="filter-section">
                                                <div className="section-header">
                                                    <h4>Carrier</h4>
                                                    <button className="section-btn" type="button" onClick={() => { handleFilterReset("carrier"); setSelectedCarriers([]); }}>Reset</button>
                                                </div>
                                                <MultiSelectChipInput
                                                    selected={selectedCarriers}
                                                    options={allCarrierOptions}
                                                    placeholder="Select carriers"
                                                    searchPlaceholder="Search carrier"
                                                    onToggle={val => setSelectedCarriers(prev =>
                                                        prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                                                    )}
                                                    onSelectAll={() => setSelectedCarriers(prev =>
                                                        prev.length === allCarrierOptions.length ? [] : [...allCarrierOptions]
                                                    )}
                                                    onReset={() => setSelectedCarriers([])}
                                                />
                                            </div>

                                            {/* Schedule Date */}
                                            <div className="filter-section position-relative">
                                                <div className="section-header">
                                                    <h4>Schedule Date</h4>
                                                    <button className="section-btn" type="button" onClick={handleScheduleClear}>Reset</button>
                                                </div>
                                                {/* <label>Filter by Delivered Date</label> */}
                                                <div className="date-input-wrapper" ref={scheduleInputRef}>
                                                    <input
                                                        type="text"
                                                        placeholder="Select date range"
                                                        readOnly
                                                        className="form-control"
                                                        value={
                                                            selectedScheduleDateRange.startDate && selectedScheduleDateRange.endDate
                                                                ? `${formatDate(selectedScheduleDateRange.startDate)} - ${formatDate(selectedScheduleDateRange.endDate)}`
                                                                : ""
                                                        }
                                                        onClick={handleSchedulePickerToggle}
                                                    />
                                                    <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M13.5 1.51172H12.7266V0H11.25V1.51172H3.72656V0H2.25V1.51172H1.47656C0.667969 1.51172 0 2.17969 0 3.02344V15.0117C0 15.8203 0.667969 16.5234 1.47656 16.5234H13.5C14.3086 16.5234 14.9766 15.8203 14.9766 15.0117V3.02344C14.9766 2.17969 14.3086 1.51172 13.5 1.51172ZM13.5 15.0117H1.47656V6.75H13.5V15.0117ZM13.5 5.27344H1.47656V3.02344H13.5V5.27344Z" fill="#9CA3AF" />
                                                    </svg>
                                                </div>

                                                {isScheduleDatePickerOpen && (
                                                    <div className="datepicker-menu" ref={scheduleDatePickerRef} style={schedulePickerStyle}>
                                                        <DateRange
                                                            ranges={[tempScheduleRange]}
                                                            editableDateInputs={true}
                                                            onChange={(item: any) =>
                                                                handleScheduleDateChange(
                                                                    item.selection.startDate ?? null,
                                                                    item.selection.endDate ?? null
                                                                )
                                                            }
                                                            moveRangeOnFirstSelection={false}
                                                            months={2}
                                                            direction="horizontal"
                                                            showDateDisplay={false}
                                                        />

                                                        <div className="datepicker-menu-bottom">
                                                            <button type="button" onClick={handleScheduleCancel} className="datepicker-btn">
                                                                CANCEL
                                                            </button>
                                                            <button type="button" onClick={handleScheduleOk} className="datepicker-btn">
                                                                OK
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Company */}
                                            <div className="filter-section">
                                                <div className="section-header">
                                                    <h4>Account Owner</h4>
                                                    <button className="section-btn" type="button" onClick={() => { handleFilterReset("companyName"); setSelectedCompanies([]); }}>Reset</button>
                                                </div>
                                                <MultiSelectChipInput
                                                    selected={selectedCompanies}
                                                    options={allCompanyOptions}
                                                    placeholder="Select account owners"
                                                    searchPlaceholder="Search account owner"
                                                    onToggle={val => setSelectedCompanies(prev =>
                                                        prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                                                    )}
                                                    onSelectAll={() => setSelectedCompanies(prev =>
                                                        prev.length === allCompanyOptions.length ? [] : [...allCompanyOptions]
                                                    )}
                                                    onReset={() => setSelectedCompanies([])}
                                                />
                                            </div>

                                            {/* Producer */}
                                            <div className="filter-section">
                                                <div className="section-header">
                                                    <h4>Producer</h4>
                                                    <button className="section-btn" type="button" onClick={() => { handleFilterReset("producer"); setSelectedProducers([]); }}>Reset</button>
                                                </div>
                                                <MultiSelectChipInput
                                                    selected={selectedProducers}
                                                    options={allProducerOptions}
                                                    placeholder="Select producers"
                                                    searchPlaceholder="Search producer"
                                                    onToggle={val => setSelectedProducers(prev =>
                                                        prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                                                    )}
                                                    onSelectAll={() => setSelectedProducers(prev =>
                                                        prev.length === allProducerOptions.length ? [] : [...allProducerOptions]
                                                    )}
                                                    onReset={() => setSelectedProducers([])}
                                                />
                                            </div>

                                            {/* Lot */}
                                            <div className="filter-section">
                                                <div className="section-header">
                                                    <h4>LOT(s)</h4>
                                                    <button className="section-btn" type="button" onClick={() => { handleFilterReset("lotNo"); setSelectedLotNos([]); }}>Reset</button>
                                                </div>
                                                <MultiSelectChipInput
                                                    selected={selectedLotNos}
                                                    options={allLotNoOptions}
                                                    placeholder="Select LOT(s)"
                                                    searchPlaceholder="Search lot no"
                                                    onToggle={val => setSelectedLotNos(prev =>
                                                        prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                                                    )}
                                                    onSelectAll={() => setSelectedLotNos(prev =>
                                                        prev.length === allLotNoOptions.length ? [] : [...allLotNoOptions]
                                                    )}
                                                    onReset={() => setSelectedLotNos([])}
                                                />
                                            </div>

                                            {/* Proof Gallon */}
                                            <div className="filter-section">
                                                <div className="section-header">
                                                    <h4>Proof Gallon</h4>
                                                    <button className="section-btn" type="button" onClick={() => handleFilterReset("totalProofGallon")}>Reset</button>
                                                </div>
                                                {/* <label>Select Proof Gallon</label> */}
                                                <input
                                                    type="number"
                                                    min="0"
                                                    placeholder="Enter proof gallon"
                                                    className="form-control"
                                                    value={filter?.totalProofGallon ?? ""}
                                                    onKeyDown={(e) =>
                                                        (e.key === '-' || (e.key === 'ArrowDown' && Number(filter.totalProofGallon) <= 0)) &&
                                                        e.preventDefault()
                                                    }
                                                    onPaste={(e) => {
                                                        const paste = e.clipboardData.getData('text');
                                                        if (paste.includes('-')) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(event) => handleFilterChange(event, "totalProofGallon")}
                                                />
                                            </div>

                                            {/* Volume */}
                                            <div className="filter-section">
                                                <div className="section-header">
                                                    <h4>Volume</h4>
                                                    <button className="section-btn" type="button" onClick={() => handleFilterReset("totalVolume")}>Reset</button>
                                                </div>
                                                {/* <label>Select Volume</label> */}
                                                <input
                                                    type="number"
                                                    min="0"
                                                    placeholder="Enter volume"
                                                    className="form-control"
                                                    value={filter.totalVolume ?? ""}
                                                    onKeyDown={(e) =>
                                                        (e.key === '-' || (e.key === 'ArrowDown' && Number(filter.totalVolume) <= 0)) &&
                                                        e.preventDefault()
                                                    }
                                                    onPaste={(e) => {
                                                        const paste = e.clipboardData.getData('text');
                                                        if (paste.includes('-')) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(event) => handleFilterChange(event, "totalVolume")}
                                                />
                                            </div>

                                        </div>
                                        <div className="filter-bottom">
                                            <div className="filter-actions">
                                                <button className="filter-btn" onClick={handleApply}><CheckIcon /> Apply</button>
                                            </div>
                                        </div>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    <div className="userTable">
                        <DataGrid
                            rows={allRequests}
                            columns={columns}
                            className="data-grid support-req--listing logistic-req-grid"
                            paginationModel={{ page, pageSize }}
                            pagination
                            paginationMode="server"
                            sortingMode="server"
                            rowHeight={60}
                            rowCount={totalCount}
                            onSortModelChange={handleSortChange}
                            onPaginationModelChange={onPaginationModelChange}
                            disableRowSelectionOnClick={true}
                            loading={isLoading}
                            slots={{
                                loadingOverlay: DataGridLoader,
                            }}
                            pageSizeOptions={[10, 20, 50, 100]}
                            onRowClick={handleRowClick}
                            disableColumnResize
                            getRowHeight={() => "auto"}
                            columnVisibilityModel={{
                                carrier: false,
                                scheduleDate: false,
                                facilityFrom: false,
                                facilityTo: false,
                                producer: false,
                                lotNo: false,
                                totalVolume: false,
                                totalProofGallon: false
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default LogisticRequestListing;